import { useCallback, useEffect, useRef, useState } from 'react';
import { liveSyncThreshold } from '../constants/dashboardConstants';
import { AnalyticsEvents } from '../enums/AnalyticsEvents';
import { useAnalytics } from './useAnalytics';

const LIVE_SYNC_INTERVAL_MS = 60 * 1000;

interface UseLiveSyncParams {
  // Refetches the dashboard, called once per interval while on
  refetch: () => void;
  // True while the dashboard request is in flight
  isUpdating: boolean;
}

/**
 * Live sync for the analytics dashboards. Refetches every 60 seconds and
 * turns itself off after liveSyncThreshold syncs, raising the limit modal.
 */
export const useLiveSync = ({ refetch, isUpdating }: UseLiveSyncParams) => {
  const { track } = useAnalytics();
  const [isEnabled, setIsEnabledState] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(() => Date.now());
  const [secondsSinceUpdate, setSecondsSinceUpdate] = useState(0);

  // The interval outlives renders, so it calls the latest refetch through a
  // ref. A new date range or page swaps the query behind it.
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  const setIsEnabled = useCallback(
    (enabled: boolean) => {
      setIsEnabledState(enabled);
      if (enabled) {
        setLastUpdatedAt(Date.now());
        track(AnalyticsEvents.DATE_RANGE_CHANGED, {
          action: 'live_sync_enabled',
        });
      }
    },
    [track]
  );

  // A finished request is a fresh update
  const wasUpdatingRef = useRef(isUpdating);
  useEffect(() => {
    if (wasUpdatingRef.current && !isUpdating) {
      setLastUpdatedAt(Date.now());
    }
    wasUpdatingRef.current = isUpdating;
  }, [isUpdating]);

  useEffect(() => {
    if (!isEnabled) {
      setSecondsSinceUpdate(0);
      return;
    }
    const tick = () =>
      setSecondsSinceUpdate(
        Math.max(0, Math.floor((Date.now() - lastUpdatedAt) / 1000))
      );
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [isEnabled, lastUpdatedAt]);

  // Each switch on starts a fresh count
  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    let syncCount = 0;
    const timer = setInterval(() => {
      refetchRef.current();
      syncCount += 1;
      if (syncCount >= liveSyncThreshold) {
        setIsEnabledState(false);
        setShowLimitModal(true);
      }
    }, LIVE_SYNC_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isEnabled]);

  return {
    isEnabled,
    setIsEnabled,
    isUpdating,
    secondsSinceUpdate,
    showLimitModal,
    setShowLimitModal,
  };
};
